import { InvalidUrlError } from '../../../common/errors/errors';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { SlotMapper } from '../mappers/SlotMapper';
import { Slot } from '../models/Slot';

// TODO(a/1204346135141215) Support sorting
export const GET_SLOTS_FOR_PROJECT_QUERY = `
query(
  $projectId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: SlotsFilter,
  $sorters: [SlotsSorter],
) {
  node(id: $projectId) {
    ... on Project {
      slots(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters,
        sorters: $sorters,
      ) {
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        totalCount
        edges {
          node {
            id
            name
            description
            traceType
            tags
            lastReport {
              __typename
              id
              name
              createdAt
              trace {
                id
                name
              }
              profile {
                id
                name
                type
              }
            }
            reportCount
          }
        }
      }
    }
  }
}
`;

export class SlotRepository extends BaseGQLService {
  private readonly mapper = new SlotMapper();

  async getSlotsForProject(projectId: number, cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<PaginationResult<Slot>> {
    return this.query(
      GET_SLOTS_FOR_PROJECT_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        ...this.mapCursorInfo(cursor),
        filters,
        sorters: this.getSorters(sorters),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Project ${projectId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.slots;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  /**
   * Slots are returned by the API sorted by ascending ID (oldest-newest).
   * Unless explicit sorters are requested, sort slots from newest to oldest.
   *
   * @param sorters the requested sorters
   * @returns the requested sorters if any, otherwise the default sorter
   */
  private getSorters(sorters: SorterSet[]): SorterSet[] {
    if (sorters.length > 0) {
      return sorters;
    }
    return [{ field: 'id', order: 'DESC' }];
  }
}
